/**
 * Checkbox Component V2
 *
 * WCAG 2.1 Level AA + BFSG konform
 */

import { forwardRef, useId, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface CheckboxProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type'> {
  /**
   * Checkbox label
   */
  label?: string;

  /**
   * Helper text
   */
  helperText?: string;

  /**
   * Error message
   */
  error?: string;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Checkbox Component
 *
 * Features:
 * - WCAG 2.1 AA compliant (keyboard navigation, ARIA labels)
 * - BFSG compliant (44x44px minimum touch target)
 * - Associated label for screen readers
 * - Error state with aria-invalid
 * - Dark/Light mode support
 *
 * @example
 * ```tsx
 * <Checkbox
 *   label="Newsletter abonnieren"
 *   helperText="Ich möchte über neue Produkte informiert werden"
 *   checked={newsletter}
 *   onChange={(e) => setNewsletter(e.target.checked)}
 * />
 * ```
 */
export const Checkbox = forwardRef<ElementRef<'input'>, CheckboxProps>(
  (
    {
      label,
      helperText,
      error,
      className,
      id,
      disabled,
      required,
      checked,
      onChange,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const autoId = useId();
    const checkboxId = id || autoId;
    const errorId = `${checkboxId}-error`;
    const helperId = `${checkboxId}-helper`;
    const hasError = Boolean(error);

    // Build input props conditionally to avoid undefined values
    const inputProps: React.InputHTMLAttributes<HTMLInputElement> = {
      ...props,
      type: 'checkbox',
      id: checkboxId,
      disabled,
      required,
      className: 'sr-only peer',
      'aria-invalid': hasError,
      'aria-describedby': cn(error && errorId, helperText && helperId),
      'aria-required': required,
    };

    // Only add checked/onChange if they are defined
    if (checked !== undefined) {
      inputProps.checked = checked;
    }
    if (onChange !== undefined) {
      inputProps.onChange = onChange;
    }

    return (
      <div className="flex items-start gap-3">
        {/* Checkbox Input (Hidden but accessible) */}
        <input ref={ref} {...inputProps} />

        {/* Custom Checkbox Visual */}
        <label
          htmlFor={checkboxId}
          className={cn(
            // Container
            'relative flex shrink-0 cursor-pointer',
            'h-5 w-5 rounded',
            'border-2',
            'transition-all duration-150',

            // BFSG: Touch target (via padding on label wrapper)
            'after:absolute after:inset-0 after:-m-2.5 after:min-h-[44px] after:min-w-[44px]',

            // Default state
            'border-neutral-400 dark:border-neutral-600',
            'bg-white dark:bg-neutral-900',

            // Hover state
            'peer-hover:border-primary-500',

            // Checked state
            'peer-checked:bg-primary-600 peer-checked:border-primary-600',

            // Focus state (WCAG 2.1: 3:1 contrast minimum)
            'peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2',

            // Disabled state
            'peer-disabled:cursor-not-allowed peer-disabled:opacity-50',

            // Error state
            hasError && 'border-red-600 peer-checked:bg-red-600 peer-checked:border-red-600',

            className
          )}
        >
          {/* Checkmark Icon */}
          <svg
            className={cn(
              'absolute inset-0 m-auto h-3 w-3',
              'text-white',
              'opacity-0 peer-checked:opacity-100',
              'transition-opacity duration-150'
            )}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </label>

        {/* Label and Helper Text */}
        {(label || helperText || error) && (
          <div className="flex flex-col gap-1">
            {label && (
              <label
                htmlFor={checkboxId}
                className={cn(
                  'text-sm font-medium cursor-pointer',
                  'text-neutral-700 dark:text-neutral-300',
                  disabled && 'cursor-not-allowed opacity-50'
                )}
              >
                {label}
                {required && (
                  <span className="text-red-600 ml-1" aria-label={t('common.required')}>
                    *
                  </span>
                )}
              </label>
            )}

            {/* Error Message */}
            {error && (
              <p
                id={errorId}
                className="text-sm text-red-600"
                role="alert"
              >
                {error}
              </p>
            )}

            {/* Helper Text */}
            {!error && helperText && (
              <p
                id={helperId}
                className="text-sm text-neutral-500 dark:text-neutral-400"
              >
                {helperText}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
